import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Calendar, Quote } from "lucide-react";

/**
 * Concept card with a layout-aware mini-screenshot. The thumbnail mirrors
 * the actual preview layout (not a generic faux nav), and uses the hero
 * image as a background where appropriate.
 */
export default function ConceptCard({ concept, index = 0 }) {
  const t = concept.theme;
  const delay = (index % 4) + 1;

  return (
    <Link
      to={`/concepts/${concept.slug}`}
      data-testid={`concept-card-${concept.slug}`}
      className={`group block rounded-2xl border border-[#EBE8DF] bg-white overflow-hidden hover:border-[#C5A059] hover:-translate-y-1 hover:shadow-[0_18px_50px_-22px_rgba(197,160,89,0.45)] transition-all duration-300 fade-up delay-${delay}`}
    >
      {/* Mock browser window */}
      <div className="border-b border-[#EBE8DF]">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#FDFBF7] border-b border-[#EBE8DF]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E76F51]"/>
          <span className="w-2.5 h-2.5 rounded-full bg-[#E9C46A]"/>
          <span className="w-2.5 h-2.5 rounded-full bg-[#5FB47A]"/>
          <span className="ml-4 flex-1 text-center text-[11px] text-[#8B8C89] font-mono truncate">{concept.domain}</span>
        </div>

        {/* Layout-specific mini screenshot */}
        <ThumbVariant concept={concept} t={t}/>
      </div>

      {/* Card meta */}
      <div className="p-6">
        <p className="label-eyebrow">{concept.industry}</p>
        <div className="flex items-start justify-between gap-4 mt-2">
          <h3 className="font-display text-2xl leading-tight">{concept.name}</h3>
          <ArrowUpRight size={18} className="text-[#8B8C89] group-hover:text-[#C5A059] group-hover:rotate-12 transition-all shrink-0 mt-1"/>
        </div>
        <p className="mt-3 text-[14px] text-[#5C5C5C] leading-relaxed">{concept.short}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-[13px] tracking-wide font-medium text-[#1A1A1A] group-hover:text-[#C5A059] transition-colors">
          View concept <ArrowUpRight size={14}/>
        </span>
      </div>
    </Link>
  );
}

function ThumbVariant({ concept, t }) {
  const heroImg = concept.images?.hero;
  const wrap = "relative aspect-[16/10] overflow-hidden flex flex-col";

  if (concept.layout === "service-pro") {
    // dark hero w/ photo + small floating "Get a quote" form card on right
    return (
      <div className={wrap} style={{ background: t.thumbBg, color: t.text, fontFamily: t.fontBody }}>
        <div className="absolute inset-0 opacity-55" style={{ background: `url(${heroImg}) center/cover` }}/>
        <div className="absolute inset-0" style={{ background: `linear-gradient(95deg, ${t.bg}EE 0%, ${t.bg}99 55%, transparent 100%)` }}/>
        <div className="relative z-10 p-4 flex items-center justify-between text-[10px]" style={{ color: t.muted }}>
          <span style={{ fontFamily: t.fontDisplay, fontSize: 13, color: t.text }}>{concept.name}</span>
          <span className="px-2.5 py-1 rounded-full text-[9px]" style={{ background: t.accent, color: "#fff" }}>
            Call
          </span>
        </div>
        <div className="relative z-10 flex-1 grid grid-cols-12 gap-2 px-4 pb-4">
          <div className="col-span-7 flex flex-col justify-center">
            <p className="text-[8px] uppercase tracking-[0.2em]" style={{ color: t.accent }}>Free 24-hr quote</p>
            <p className="mt-1.5 leading-[1.05]" style={{ fontFamily: t.fontDisplay, fontSize: 18 }}>
              {concept.hero.title.split(".")[0]}.
            </p>
          </div>
          <div className="col-span-5 self-center p-2 rounded-md border" style={{ background: t.surface + "F0", borderColor: t.muted + "44" }}>
            <p className="text-[7px] uppercase tracking-[0.18em]" style={{ color: t.accent }}>Free quote</p>
            <div className="mt-1.5 space-y-1">
              <div className="h-1.5 rounded" style={{ background: t.muted + "33" }}/>
              <div className="h-1.5 rounded" style={{ background: t.muted + "33" }}/>
              <div className="h-1.5 rounded" style={{ background: t.muted + "33" }}/>
            </div>
            <div className="mt-1.5 py-1 rounded text-center text-[7px]" style={{ background: t.accent, color: "#fff" }}>
              Send
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (concept.layout === "modern-tech") {
    // light gradient hero w/ image right + floating booking card pill
    return (
      <div className={wrap} style={{ background: t.heroBg, color: t.text, fontFamily: t.fontBody }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-25" style={{ background: t.accent }}/>
        <div className="relative z-10 p-4 flex items-center justify-between text-[10px]" style={{ color: t.muted }}>
          <span style={{ fontFamily: t.fontDisplay, fontSize: 13, color: t.text }}>{concept.name}</span>
          <span className="px-2.5 py-1 rounded-full text-[9px]" style={{ background: t.accent, color: "#fff" }}>
            Book
          </span>
        </div>
        <div className="relative z-10 flex-1 grid grid-cols-12 gap-3 px-4 pb-4 items-center">
          <div className="col-span-7">
            <span className="inline-block px-2 py-0.5 rounded-full text-[8px]" style={{ background: t.accentSoft, color: t.accent }}>
              Same-day service
            </span>
            <p className="mt-2 leading-[1.05]" style={{ fontFamily: t.fontDisplay, fontSize: 17 }}>
              Comfort, restored — fast.
            </p>
            <div className="mt-2.5 inline-block px-2.5 py-1 rounded-full text-[8px]" style={{ background: t.accent, color: "#fff" }}>
              Book a call →
            </div>
          </div>
          <div className="col-span-5">
            <div className="rounded-lg overflow-hidden relative aspect-[4/3]" style={{ background: `url(${heroImg}) center/cover` }}/>
            <div className="-mt-3 ml-2 px-2 py-1.5 rounded-md flex items-center gap-1.5 bg-white" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", border: `1px solid ${t.muted}22` }}>
              <Calendar size={9} style={{ color: t.accent }}/>
              <span className="text-[8px]" style={{ color: t.text }}>Today · 2 slots</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (concept.layout === "editorial-calm") {
    // soft split — image LEFT, copy RIGHT (different from service-pro)
    return (
      <div className={wrap} style={{ background: t.heroBg, color: t.text, fontFamily: t.fontBody }}>
        <div className="relative z-10 p-4 flex items-center justify-between text-[10px]" style={{ color: t.muted }}>
          <span style={{ fontFamily: t.fontDisplay, fontSize: 14, color: t.text }}>{concept.name}</span>
          <span className="px-2.5 py-1 rounded-full text-[9px]" style={{ background: t.accent, color: "#fff" }}>
            Book
          </span>
        </div>
        <div className="relative z-10 flex-1 grid grid-cols-12 gap-3 px-4 pb-4 items-center">
          <div className="col-span-5">
            <div className="rounded-md overflow-hidden aspect-[4/5]" style={{ background: `url(${heroImg}) center/cover` }}/>
          </div>
          <div className="col-span-7">
            <p className="text-[8px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>New patients welcome</p>
            <p className="mt-1.5 leading-[1.05]" style={{ fontFamily: t.fontDisplay, fontSize: 17 }}>
              {concept.hero.title}
            </p>
            <div className="mt-3 inline-block px-2.5 py-1 rounded-full text-[8px]" style={{ background: t.accent, color: "#fff" }}>
              {concept.hero.ctaPrimary} →
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (concept.layout === "premium-authority") {
    // centered serif hero + thin photo strip at bottom
    return (
      <div className={wrap} style={{ background: t.heroBg, color: t.text, fontFamily: t.fontBody }}>
        <div className="relative z-10 p-4 flex items-center justify-between text-[10px]" style={{ color: t.muted }}>
          <span style={{ fontFamily: t.fontDisplay, fontSize: 14, color: t.text }}>{concept.name}</span>
          <span className="text-[8px] uppercase tracking-[0.18em]" style={{ color: t.muted }}>Practice · Results · Attorneys</span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 text-center">
          <p className="text-[8px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Trial-tested attorneys</p>
          <p className="mt-2 leading-[1.02]" style={{ fontFamily: t.fontDisplay, fontSize: 22 }}>
            {concept.hero.title}
          </p>
          <div className="mt-3 flex gap-2">
            <span className="px-2.5 py-1 rounded-full text-[8px]" style={{ background: t.accent, color: "#fff" }}>Consult</span>
            <span className="px-2.5 py-1 rounded-full text-[8px] border" style={{ borderColor: t.muted, color: t.text }}>Results</span>
          </div>
        </div>
        <div className="relative z-10 h-12 mx-3 mb-3 rounded-md" style={{ background: `url(${heroImg}) center/cover` }}/>
      </div>
    );
  }

  if (concept.layout === "portfolio") {
    // full-bleed image with bottom-aligned title (no faux nav clutter)
    return (
      <div className={wrap} style={{ background: `url(${heroImg}) center/cover, ${t.thumbBg}` }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.65) 100%)" }}/>
        <div className="relative z-10 p-4 flex items-center justify-between text-[10px] text-white opacity-90">
          <span style={{ fontFamily: t.fontDisplay, fontSize: 14 }}>{concept.name}</span>
          <span className="px-2.5 py-1 rounded-full text-[9px]" style={{ background: t.accent }}>Portfolio</span>
        </div>
        <div className="relative z-10 mt-auto p-4 text-white">
          <p className="text-[8px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Award-winning design-build</p>
          <p className="mt-1.5 leading-[1.02]" style={{ fontFamily: t.fontDisplay, fontSize: 22 }}>
            {concept.hero.title}
          </p>
        </div>
      </div>
    );
  }

  if (concept.layout === "emergency-action") {
    // light hero w/ HUGE phone-number CTA
    return (
      <div className={wrap} style={{ background: t.heroBg, color: t.text, fontFamily: t.fontBody }}>
        <div className="absolute inset-x-0 top-0 py-1.5 text-center text-[8px] uppercase tracking-[0.22em]"
             style={{ background: t.accent, color: "#fff" }}>
          24/7 emergency — call now
        </div>
        <div className="relative z-10 pt-7 p-4 flex items-center justify-between text-[10px]" style={{ color: t.muted }}>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full flex items-center justify-center" style={{ background: t.accent, color: "#fff" }}>
              <span style={{ fontSize: 7 }}>💧</span>
            </span>
            <span style={{ fontFamily: t.fontDisplay, fontSize: 13, color: t.text }}>{concept.name}</span>
          </span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 pb-4">
          <p className="text-[8px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>Call now</p>
          <div className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: t.accent, color: "#fff" }}>
            <Phone size={14}/>
            <span style={{ fontFamily: t.fontDisplay, fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" }}>
              {concept.phone}
            </span>
          </div>
          <p className="mt-2 text-[8px]" style={{ color: t.muted }}>Or book online · Avg dispatch 60 min</p>
        </div>
      </div>
    );
  }

  // fallback
  return (
    <div className={wrap} style={{ background: t.thumbBg, color: t.text, fontFamily: t.fontBody }}>
      <div className="p-4">{concept.name}</div>
    </div>
  );
}
